import { KTitleSpan, KSpan, KButton, KInput } from "kahuna-base-react-components"
import React, { useEffect, useState } from "react"
import { SmartLinkArtist, SelectOption, ArtistSearch, Artist } from "../../../../types"
import { featureFm, releases } from "../../../../apis"
import { mapListToCamelCase, mapToCamelCase, mapToSnakeCase } from "../../../../utility"
import { toast } from "react-toastify"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import { lang } from "../../../../constants/languages"

const initialSmartLinkArtistForm: SmartLinkArtist = {
  artistName: "",
  countryCode: "",
  spotifyArtistUrl: ""
}

const ArtistSelectPage = ({
  onSelectArtist,
  selectedArtist
}: {
  onSelectArtist: (fmArtistId?: string) => void
  selectedArtist?: string
}) => {
  const [smartLinkArtistList, setSmartLinkArtistList] = useState<SmartLinkArtist[]>([])
  const [smartLinkArtist, setSmartLinkArtist] = useState<SmartLinkArtist>(initialSmartLinkArtistForm)
  const [addNewOpen, setAddNewOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [timeoutId, setTimeoutId] = useState(null)
  const [artistOptions, setArtistOptions] = useState<SelectOption[]>([])
  const [searchText, setSearchText] = useState<string>("")
  const [spotifySearchLoading, setSpotifySearchLoading] = useState<boolean>(false)

  const fetchSmartLinkArtistList = () => {
    featureFm(`/smart_link_artists/`, "GET").then((resp) => {
      const smartLinkArtists: SmartLinkArtist[] = mapListToCamelCase(resp.data.results)
      setSmartLinkArtistList(smartLinkArtists)
    })
  }

  useEffect(() => {
    fetchSmartLinkArtistList()
  }, [])

  const onSave = (smartLinkArtist: SmartLinkArtist) => {
    if (!smartLinkArtist.countryCode || !smartLinkArtist.artistName || !smartLinkArtist.spotifyArtistUrl) {
      toast.error(lang.catalog.smart_link.error.empty_fields, errorFilledToastOptions)
      return
    }
    if (smartLinkArtist.countryCode.length !== 2) {
      toast.error(lang.catalog.smart_link.error.country_code_min_2_character, errorFilledToastOptions)
      return
    }
    if (!smartLinkArtist.spotifyArtistUrl.startsWith("https://open.spotify.com/artist/")) {
      toast.error(lang.catalog.smart_link.error.spotify_artist_url_fix, errorFilledToastOptions)
      return
    }
    setLoading(true)
    featureFm(`/save_smart_link_artist/`, "POST", { ...mapToSnakeCase(smartLinkArtist) })
      .then((resp) => {
        setLoading(false)
        if (resp.data.result) {
          toast.success(lang.catalog.smart_link.success.smart_link_save_artist, successFilledToastOptions)
          if (smartLinkArtist.id) {
            setSmartLinkArtistList(
              smartLinkArtistList.map((sla) => (sla.id === smartLinkArtist.id ? { ...smartLinkArtist } : sla))
            )
          } else {
            const result = mapToCamelCase(resp.data.result)
            smartLinkArtist.id = result.id
            smartLinkArtist.featureFmId = result.featureFmId
            setSmartLinkArtistList([smartLinkArtist, ...smartLinkArtistList])
            setAddNewOpen(false)
          }
          setSmartLinkArtist(initialSmartLinkArtistForm)
        } else {
          toast.error(
            // @ts-ignore
            `${lang.catalog.smart_link.error[resp.data.message]}. ${lang.common.contact_customer_support}`,
            errorFilledToastOptions
          )
        }
      })
      .catch(() => {
        setLoading(false)
        toast.error(lang.catalog.smart_link.error.error_smart_link_artist_save, errorFilledToastOptions)
      })
  }

  const onEdit = (smartLinkArtist: SmartLinkArtist) => {
    setAddNewOpen(false)
    setSmartLinkArtist(smartLinkArtist)
  }

  const onSearchArtist = (search: string) => {
    setSearchText(search)
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    setSpotifySearchLoading(true)

    setTimeoutId(
      // @ts-ignore
      setTimeout(async () => {
        if (!search) {
          setSpotifySearchLoading(false)
          setArtistOptions([])
          return
        }
        releases(`/artist/search/?value=${search}&sp_enabled=1`, "get")
          .then((resp) => {
            setSpotifySearchLoading(false)
            if (resp?.data?.result) {
              const artistSearch: ArtistSearch = mapToCamelCase(resp.data.result)
              const spotifyOptions: SelectOption[] = artistSearch.spotify.map((option: Artist) => {
                return {
                  value: option.name,
                  label: option.name,
                  spotifyId: option.spotifyId,
                  cover: option.cover || "/reports_icons/avatar.svg"
                }
              })

              setArtistOptions(spotifyOptions)
            } else {
              setArtistOptions([])
            }
          })
          .catch(() => {
            setSpotifySearchLoading(false)
            setArtistOptions([])
          })
      }, 1000)
    )
  }

  const onCancel = () => {
    setAddNewOpen(false)
    setSmartLinkArtist(initialSmartLinkArtistForm)
  }

  const renderSearchSpotify = () => {
    return (
      <div className="mb-3">
        <div>
          <KSpan text={lang.catalog.smart_link.search_spotify_artist} />
        </div>
        <div className="mt-1">
          <KInput
            placeholder={lang.catalog.smart_link.search_placeholder}
            onChange={(value) => onSearchArtist(value)}
            value={searchText}
          />
        </div>
        <div>
          {spotifySearchLoading && (
            <div className="mt-1">
              <KSpan text={lang.catalog.smart_link.searching} />
            </div>
          )}
          {artistOptions.length > 0 && (
            <div className="mt-1 flex flex-col">
              {artistOptions.map((option) => (
                <div
                  key={option.value}
                  className="mt-1 flex gap-2 items-center hover:bg-gray-100 cursor-pointer pt-1 pb-1 pr-2 pl-2 rounded"
                  onClick={() => {
                    setSmartLinkArtist({
                      artistName: option.value?.toString() || "",
                      artistImage: option.cover,
                      countryCode: "",
                      spotifyArtistUrl: `https://open.spotify.com/artist/${option.spotifyId}`
                    })
                    setArtistOptions([])
                  }}
                >
                  <img src={option.cover} style={{ width: 40, borderRadius: 5 }} />
                  <KSpan text={option.value?.toString() || ""} fontSize={16} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderForm = (sla: SmartLinkArtist) => {
    if (sla.id !== smartLinkArtist.id) {
      return (
        <div className="flex justify-between" key={`sla_view_${sla.id}`} style={{ borderTop: "1px solid #9b9b9b" }}>
          <div className="flex flex-col gap-2 pt-2 pb-2">
            <div className="flex gap-2">
              <img
                src={sla.artistImage || "/admin_icons/user.svg"}
                style={{ width: 46, height: 46, borderRadius: 5 }}
              />
              <div className="flex flex-col gap-2">
                <KSpan
                  text={`${lang.catalog.smart_link.artist_name}: ${sla.artistName}`}
                  fontSize={14}
                  lineHeight="20px"
                  color="#000"
                />
                <KSpan
                  text={`${lang.catalog.smart_link.country_code}: ${sla.countryCode}`}
                  fontSize={14}
                  lineHeight="20px"
                  color="#000"
                />
              </div>
            </div>
            <KSpan
              text={`${lang.catalog.smart_link.spotify_artist_url}: ${sla.spotifyArtistUrl}`}
              fontSize={14}
              lineHeight="20px"
              color="#000"
            />
          </div>

          <div className="flex flex-col items-center justify-center ml-20">
            <div className="flex flex-row gap-4 mr-2">
              <KButton
                background={selectedArtist === sla.featureFmId ? "#bebebe" : "black"}
                textColor={"white"}
                onClick={() => onSelectArtist(sla.featureFmId)}
                text={selectedArtist === sla.featureFmId ? lang.common.selected : lang.common.select}
              />
              <KButton background="transparent" onClick={() => onEdit(sla)} icon="/account_icons/edit.svg" />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div
        className="flex justify-between pt-2 pb-2"
        key={`sla_edit_${sla.id}`}
        style={{ borderTop: "1px solid #9b9b9b" }}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-2" style={{ width: "100%" }}>
            <KInput
              width={350}
              value={smartLinkArtist.artistName || ""}
              onChange={(value) => setSmartLinkArtist({ ...smartLinkArtist, artistName: value })}
              placeholder={lang.catalog.smart_link.artist_name}
            />
            <KInput
              width={120}
              value={smartLinkArtist.countryCode || ""}
              onChange={(value) =>
                value.length <= 2 && setSmartLinkArtist({ ...smartLinkArtist, countryCode: value.toUpperCase() })
              }
              placeholder={`${lang.catalog.smart_link.country_code} (XX)`}
            />
          </div>
          <div>
            <KInput
              width={470}
              value={smartLinkArtist.spotifyArtistUrl || ""}
              onChange={(value) => setSmartLinkArtist({ ...smartLinkArtist, spotifyArtistUrl: value })}
              placeholder={`${lang.catalog.smart_link.spotify_artist_url} (https://open.spotify.com/artist/xxxxx)`}
            />
          </div>
        </div>
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center ml-20 gap-4 mr-2">
            <KButton
              disabled={loading}
              background="black"
              textColor="white"
              onClick={() => onSave(smartLinkArtist)}
              text={lang.common.save}
            />
            <KButton
              disabled={loading}
              background="transparent"
              icon="/account_icons/cancel.svg"
              onClick={() => onCancel()}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="rounded-[10px] bg-[#fff]"
      style={{ minWidth: 540 }}
      onKeyDown={(e) => e.key === "Escape" && setSmartLinkArtist(initialSmartLinkArtistForm)}
    >
      <div className="mb-4">
        <KTitleSpan text={lang.catalog.smart_link.artist_select_title} fontSize={32} fontWeight={500} />
      </div>
      {!addNewOpen && (
        <div className={`flex justify-start mb-3`} data-testid="feature-fm-add-new-button">
          <KButton
            background="transparent"
            onClick={() => {
              setSmartLinkArtist(initialSmartLinkArtistForm)
              setAddNewOpen(true)
            }}
            text={lang.catalog.smart_link.add_new}
          />
        </div>
      )}

      <div className="flex flex-col" style={{ maxHeight: "68vh", overflowY: "auto" }}>
        {addNewOpen && renderSearchSpotify()}
        {addNewOpen && <div className="mb-4">{renderForm(smartLinkArtist)}</div>}
        {smartLinkArtistList.map((sla) => renderForm(sla))}
      </div>
    </div>
  )
}

export default ArtistSelectPage
